// Chi tiết dự án: tiến độ, công việc, thành viên, giai đoạn, trao đổi — công việc lấy chung kho với module Công việc
import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Dropdown, Empty, Form, Input, Modal, Progress, Radio, Segmented, Select, Table, Tabs, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Link2, MoreHorizontal, Pencil, Plus, Send, Trash2, Users } from 'lucide-react';
import Layout from '../../Layout';
import { DEPARTMENTS, ME_ID, memberName, projectOf, type WorkTask } from '../WorkList/data';
import { ACTIVITY_TEXT, isClosed, isOverdue } from '../WorkList/flow';
import useWorkStore from '../WorkList/store';
import TaskDetail from '../WorkList/TaskDetail';
import TaskForm, { memberOptions } from '../WorkList/TaskForm';
import { GanttView } from '../WorkList/component/GanttView';
import { Avatar, DueText, PriorityTag, StatusTag, fmtDate } from '../WorkList/ui';
import {
  PROGRESS_METHODS, PROJECT_ACTION_LABEL, PROJECT_ACTIVITY_TEXT, PROJECT_STATUS, canManageProject, finishBlocker, isProjectClosed,
  outOfRange, participantsOf, progressOf, projectActions, projectTasks, statsOf, type Phase, type ProjectAction, type ProjectInfo,
} from './data';
import useProjectStore from './store';

type TaskFilter = 'open' | 'overdue' | 'review' | 'closed' | 'outside' | 'all';

const TASK_FILTERS: { value: TaskFilter; label: string; test: (t: WorkTask, p: ProjectInfo) => boolean }[] = [
  { value: 'open', label: 'Đang mở', test: t => !isClosed(t) },
  { value: 'overdue', label: 'Quá hạn', test: t => isOverdue(t) },
  { value: 'review', label: 'Chờ duyệt', test: t => t.status === 'review' },
  { value: 'closed', label: 'Đã đóng', test: t => isClosed(t) },
  { value: 'outside', label: 'Ngoài lịch dự án', test: (t, p) => outOfRange(p, t) },
  { value: 'all', label: 'Tất cả', test: () => true },
];

const sortTasks = (a: WorkTask, b: WorkTask) =>
  Number(isClosed(a)) - Number(isClosed(b)) || Number(isOverdue(b)) - Number(isOverdue(a)) || (a.dueAt ?? '9').localeCompare(b.dueAt ?? '9');

const Card = ({ title, extra, children, className = '' }: { title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; className?: string }) => (
  <section className={`rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 ${className}`}>
    {title && <div className="mb-3 flex items-center justify-between gap-2"><h3 className="font-semibold text-slate-800 dark:text-gray-100">{title}</h3>{extra}</div>}
    {children}
  </section>
);

const Info = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3 py-1.5 text-sm">
    <span className="w-28 shrink-0 text-slate-500">{label}</span>
    <div className="min-w-0 flex-1 text-slate-800 dark:text-gray-200">{children}</div>
  </div>
);

function Kpi({ label, value, sub, color, active, onClick }: { label: string; value: React.ReactNode; sub?: React.ReactNode; color: string; active?: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} className={`rounded-xl border bg-white p-3 text-left transition hover:border-[#3e91ab] dark:bg-gray-800 ${active ? 'border-[#3e91ab] ring-1 ring-[#3e91ab]/30' : 'border-slate-200 dark:border-gray-700'}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </button>
  );
}

// Thanh khối lượng việc: hoàn thành / đang làm / chờ duyệt / quá hạn
function LoadBar({ tasks }: { tasks: WorkTask[] }) {
  const parts = [
    { n: tasks.filter(t => t.status === 'done').length, c: '#22c55e', l: 'Hoàn thành' },
    { n: tasks.filter(t => !isClosed(t) && t.status !== 'review' && !isOverdue(t)).length, c: '#3b82f6', l: 'Đang làm / chưa bắt đầu' },
    { n: tasks.filter(t => t.status === 'review').length, c: '#a855f7', l: 'Chờ duyệt' },
    { n: tasks.filter(t => isOverdue(t)).length, c: '#ef4444', l: 'Quá hạn' },
  ];
  const total = parts.reduce((s, p) => s + p.n, 0) || 1;
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-gray-700">
      {parts.filter(p => p.n).map(p => <Tooltip key={p.l} title={`${p.l}: ${p.n}`}><div style={{ width: `${(p.n / total) * 100}%`, background: p.c }} /></Tooltip>)}
    </div>
  );
}

function StatusModal({ project, action, onClose }: { project: ProjectInfo; action: ProjectAction | null; onClose: () => void }) {
  const act = useProjectStore(s => s.act);
  const [note, setNote] = useState('');
  if (!action) return null;
  const needNote = action === 'pause' || action === 'cancel';
  const ok = () => {
    const err = act(project.id, action, ME_ID, note);
    if (err) { message.warning(err); return; }
    message.success(PROJECT_ACTION_LABEL[action]);
    onClose();
  };
  return (
    <Modal open zIndex={1100} title={PROJECT_ACTION_LABEL[action]} onCancel={onClose} onOk={ok} okText={PROJECT_ACTION_LABEL[action]} cancelText="Đóng"
      okButtonProps={{ danger: action === 'cancel', disabled: needNote && !note.trim() }}>
      {action === 'cancel' && <Alert className="mb-3" type="warning" showIcon message="Dự án huỷ sẽ không nhận thêm công việc. Các việc đang mở vẫn giữ nguyên để người làm xử lý." />}
      <Input.TextArea autoFocus rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={needNote ? 'Lý do (bắt buộc)' : 'Ghi chú (không bắt buộc)'} />
    </Modal>
  );
}

function EditModal({ project, open, onClose }: { project: ProjectInfo; open: boolean; onClose: () => void }) {
  const update = useProjectStore(s => s.update);
  const [form] = Form.useForm<{ range: [Dayjs, Dayjs]; description?: string; progressMethod: ProjectInfo['progressMethod']; syncTaskDates: boolean; taskWithinProject: boolean }>();
  const save = async () => {
    const v = await form.validateFields();
    const startAt = v.range[0].format('YYYY-MM-DD');
    const shift = project.syncTaskDates && project.startAt ? dayjs(startAt).diff(project.startAt, 'day') : 0;
    update(project.id, { startAt, endAt: v.range[1].format('YYYY-MM-DD'), description: v.description?.trim(), progressMethod: v.progressMethod, syncTaskDates: v.syncTaskDates, taskWithinProject: v.taskWithinProject }, ME_ID);
    message.success(shift ? `Đã lưu · dời các việc đang mở ${shift > 0 ? '+' : ''}${shift} ngày theo dự án` : 'Đã lưu thông tin dự án');
    onClose();
  };
  return (
    <Modal open={open} zIndex={1100} title="Sửa thông tin dự án" onCancel={onClose} onOk={save} okText="Lưu" cancelText="Huỷ" width={620} destroyOnHidden
      afterOpenChange={o => o && form.setFieldsValue({
        range: [dayjs(project.startAt), dayjs(project.endAt)], description: project.description, progressMethod: project.progressMethod,
        syncTaskDates: project.syncTaskDates, taskWithinProject: project.taskWithinProject,
      })}>
      <Form form={form} layout="vertical" className="mt-3" preserve={false}>
        <Form.Item name="range" label="Thời gian dự kiến" rules={[{ required: true, message: 'Chọn thời gian dự án' }]}
          extra={project.syncTaskDates ? 'Đổi ngày bắt đầu sẽ dời lịch các việc đang mở cùng số ngày' : undefined}>
          <DatePicker.RangePicker className="w-full" format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="description" label="Mô tả / mục tiêu"><Input.TextArea rows={3} maxLength={2000} /></Form.Item>
        <Form.Item name="progressMethod" label="Cách tính tiến độ">
          <Radio.Group className="flex flex-col gap-2">
            {PROGRESS_METHODS.map(m => <Radio key={m.value} value={m.value}>{m.label} <span className="text-xs text-slate-400">· {m.hint}</span></Radio>)}
          </Radio.Group>
        </Form.Item>
        <Form.Item name="syncTaskDates" valuePropName="checked" className="!mb-1"><Checkbox>Khi thời gian dự án thay đổi thì thời gian công việc thay đổi theo</Checkbox></Form.Item>
        <Form.Item name="taskWithinProject" valuePropName="checked" className="!mb-0"><Checkbox>Thời gian công việc phải nằm trong thời gian dự án</Checkbox></Form.Item>
      </Form>
    </Modal>
  );
}

function MembersModal({ project, open, onClose }: { project: ProjectInfo; open: boolean; onClose: () => void }) {
  const setMembers = useProjectStore(s => s.setMembers);
  const [v, setV] = useState({ ownerIds: project.ownerIds, memberIds: project.memberIds, followerIds: project.followerIds });
  const opts = memberOptions(projectOf(project.id)?.departmentId);
  return (
    <Modal open={open} zIndex={1100} title="Thành viên dự án" onCancel={onClose} okText="Lưu" cancelText="Huỷ" width={620} destroyOnHidden
      afterOpenChange={o => o && setV({ ownerIds: project.ownerIds, memberIds: project.memberIds, followerIds: project.followerIds })}
      okButtonProps={{ disabled: !v.ownerIds.length }}
      onOk={() => { setMembers(project.id, v, ME_ID); message.success('Đã cập nhật thành viên'); onClose(); }}>
      <div className="mt-3 space-y-4 text-sm">
        <div>
          <div className="mb-1 font-medium">Người quản trị <span className="text-red-500">*</span></div>
          <Select mode="multiple" className="w-full" optionFilterProp="label" options={opts} value={v.ownerIds} onChange={ownerIds => setV({ ...v, ownerIds, memberIds: v.memberIds.filter(m => !ownerIds.includes(m)) })} />
          <div className="mt-1 text-xs text-slate-400">Đổi trạng thái, thành viên, giai đoạn và thông tin dự án</div>
        </div>
        <div>
          <div className="mb-1 font-medium">Thành viên</div>
          <Select mode="multiple" className="w-full" optionFilterProp="label" options={opts.filter(o => !v.ownerIds.includes(o.value))} value={v.memberIds} onChange={memberIds => setV({ ...v, memberIds })} maxTagCount="responsive" />
          <div className="mt-1 text-xs text-slate-400">Người được giao việc trong dự án cũng tự động là người tham gia</div>
        </div>
        <div>
          <div className="mb-1 font-medium">Người theo dõi</div>
          <Select mode="multiple" className="w-full" optionFilterProp="label" options={opts} value={v.followerIds} onChange={followerIds => setV({ ...v, followerIds })} maxTagCount="responsive" />
        </div>
      </div>
    </Modal>
  );
}

function PhasesModal({ project, open, onClose }: { project: ProjectInfo; open: boolean; onClose: () => void }) {
  const setPhases = useProjectStore(s => s.setPhases);
  const [rows, setRows] = useState<Phase[]>(project.phases);
  const set = (i: number, patch: Partial<Phase>) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const invalid = rows.some(r => !r.name.trim());
  return (
    <Modal open={open} zIndex={1100} title="Giai đoạn dự án" onCancel={onClose} okText="Lưu" cancelText="Huỷ" width={760} destroyOnHidden
      afterOpenChange={o => o && setRows(project.phases)} okButtonProps={{ disabled: invalid }}
      onOk={() => { setPhases(project.id, rows.map(r => ({ ...r, name: r.name.trim() })), ME_ID); message.success('Đã lưu giai đoạn'); onClose(); }}>
      <div className="mt-3 space-y-2">
        {rows.map((r, i) => (
          <div key={r.id} className="grid grid-cols-[24px_1fr] items-start gap-2 rounded-lg border border-slate-200 p-2 sm:grid-cols-[24px_1fr_260px_32px]">
            <span className="pt-1.5 text-center text-xs font-semibold text-slate-400">{i + 1}</span>
            <div className="space-y-1">
              <Input status={!r.name.trim() ? 'error' : undefined} placeholder="Tên giai đoạn" value={r.name} onChange={e => set(i, { name: e.target.value })} />
              <Input size="small" variant="borderless" placeholder="Ghi chú / kết quả bàn giao" value={r.note} onChange={e => set(i, { note: e.target.value })} />
            </div>
            <DatePicker.RangePicker className="col-start-2 sm:col-start-auto" format="DD/MM/YYYY" allowEmpty={[true, true]}
              value={[r.startAt ? dayjs(r.startAt) : null, r.endAt ? dayjs(r.endAt) : null]}
              onChange={d => set(i, { startAt: d?.[0]?.format('YYYY-MM-DD'), endAt: d?.[1]?.format('YYYY-MM-DD') })} />
            <Button type="text" danger className="col-start-2 justify-self-end sm:col-start-auto" icon={<Trash2 size={16} />} onClick={() => setRows(rows.filter((_, k) => k !== i))} />
          </div>
        ))}
        {rows.length === 0 && <div className="py-4 text-center text-sm text-slate-400">Chưa có giai đoạn</div>}
        <Button type="dashed" block icon={<Plus size={14} />} onClick={() => setRows([...rows, { id: `ph-${Date.now().toString(36)}`, name: '', startAt: rows.at(-1)?.endAt ?? project.startAt, endAt: project.endAt }])}>Thêm giai đoạn</Button>
      </div>
    </Modal>
  );
}

export default function ProjectDetail() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const id = params.get('id') ?? '';
  const taskId = params.get('task');
  const project = useProjectStore(s => s.projects.find(p => p.id === id));
  const comment = useProjectStore(s => s.comment);
  const addLink = useProjectStore(s => s.addLink);
  const removeLink = useProjectStore(s => s.removeLink);
  const allTasks = useWorkStore(s => s.tasks);

  const [tab, setTab] = useState('tasks');
  const [filter, setFilter] = useState<TaskFilter>('open');
  const [assignee, setAssignee] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [action, setAction] = useState<ProjectAction | null>(null);
  const [modal, setModal] = useState<'edit' | 'members' | 'phases' | 'task' | null>(null);
  const [side, setSide] = useState<'activity' | 'discussion'>('activity');
  const [text, setText] = useState('');
  const [link, setLink] = useState({ name: '', url: '' });
  const [ganttAll, setGanttAll] = useState(false);

  const tasks = useMemo(() => projectTasks(id, allTasks), [id, allTasks]);
  const stats = useMemo(() => statsOf(id, allTasks), [id, allTasks]);

  const setParam = (key: string, value?: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: key !== 'task' });
  };
  const openTask = (tid: string) => setParam('task', tid);

  const feed = useMemo(() => {
    if (!project) return [];
    const own = project.activity.filter(a => a.type !== 'comment').map(a => ({
      id: a.id, at: a.at, by: a.by, color: a.type === 'status' ? '#3e91ab' : '#94a3b8',
      text: <>{PROJECT_ACTIVITY_TEXT[a.type]}{a.text && <span className="text-slate-500"> · {a.text}</span>}</>,
    }));
    const fromTasks = allTasks.filter(t => t.projectId === project.id).flatMap(t => t.activity
      .filter(a => ['create', 'submit', 'approve', 'return', 'fail', 'cancel', 'reopen'].includes(a.type))
      .map(a => ({
        id: a.id, at: a.at, by: a.by, color: a.type === 'approve' ? '#22c55e' : a.type === 'return' || a.type === 'fail' ? '#ef4444' : '#cbd5e1',
        text: <>{ACTIVITY_TEXT[a.type]} <button className="text-[#3e91ab] hover:underline" onClick={() => openTask(t.id)}>{t.code} {t.name}</button></>,
      })));
    return [...own, ...fromTasks].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 40);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, allTasks]);

  if (!project) {
    return (
      <Layout>
        <div className="py-16"><Empty description="Không tìm thấy dự án"><Link to="/projects"><Button type="primary">Về danh sách dự án</Button></Link></Empty></div>
      </Layout>
    );
  }

  const meta = projectOf(project.id);
  const manage = canManageProject(project, ME_ID);
  const actions = projectActions(project, ME_ID);
  const primary = actions.filter(a => a === 'start' || a === 'resume' || a === 'finish' || a === 'reopen');
  const more = actions.filter(a => !primary.includes(a));
  const progress = progressOf(project, allTasks);
  const today = dayjs().startOf('day');
  const totalDays = project.startAt && project.endAt ? dayjs(project.endAt).diff(project.startAt, 'day') + 1 : 0;
  const lateDays = project.endAt && !isProjectClosed(project) ? today.diff(project.endAt, 'day') : 0;
  const timePct = totalDays ? Math.min(100, Math.max(0, Math.round((today.diff(project.startAt, 'day') / totalDays) * 100))) : 0;
  const lastPause = [...project.activity].reverse().find(a => a.type === 'status' && a.text?.includes('→ Tạm dừng'));
  const outside = project.taskWithinProject ? tasks.filter(t => outOfRange(project, t)).length : 0;
  const participants = participantsOf(project, allTasks);
  const comments = project.activity.filter(a => a.type === 'comment');
  const closed = isProjectClosed(project);

  const run = (a: ProjectAction) => {
    if (a === 'finish') {
      const blocker = finishBlocker(project, allTasks);
      if (blocker) { message.warning(blocker); setTab('tasks'); setFilter('open'); return; }
    }
    setAction(a);
  };
  const showTasks = (f: TaskFilter, who?: string) => { setTab('tasks'); setFilter(f); setAssignee(who); };

  const shown = tasks
    .filter(t => TASK_FILTERS.find(f => f.value === filter)!.test(t, project) && (!assignee || t.assigneeId === assignee) && (!q || `${t.code} ${t.name}`.toLowerCase().includes(q.toLowerCase())))
    .sort(sortTasks);

  const columns: ColumnsType<WorkTask> = [
    {
      key: 'name', title: 'Công việc', render: (_, t) => (
        <span className="block min-w-[240px]">
          <span className={`font-medium ${isClosed(t) ? 'text-slate-400' : ''}`}>{t.parentId && <span className="text-slate-400">↳ </span>}{t.name}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-400"><span className="font-mono">{t.code}</span><PriorityTag t={t} />{outOfRange(project, t) && project.taskWithinProject && <Tag color="orange" className="!m-0">Ngoài lịch DA</Tag>}</span>
        </span>
      ),
    },
    { key: 'who', title: 'Người làm', width: 200, render: (_, t) => <Avatar id={t.assigneeId} withName /> },
    { key: 'due', title: 'Hạn chót', width: 130, render: (_, t) => <DueText t={t} /> },
    { key: 'status', title: 'Trạng thái', width: 120, render: (_, t) => <StatusTag t={t} /> },
  ];

  const workload = participants.map(pid => {
    const mine = tasks.filter(t => t.assigneeId === pid);
    return { id: pid, role: project.ownerIds.includes(pid) ? 'Quản trị' : project.memberIds.includes(pid) ? 'Thành viên' : 'Được giao việc', tasks: mine, open: mine.filter(t => !isClosed(t)).length, overdue: mine.filter(t => isOverdue(t)).length };
  }).sort((a, b) => b.open - a.open);

  const sendComment = () => { comment(project.id, text, ME_ID); setText(''); };
  const validUrl = /^https?:\/\/\S+$/i.test(link.url.trim());

  return (
    <Layout>
      {/* Header */}
      <div className="sticky top-[64px] z-10 -mx-4 border-b border-slate-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Link to="/projects" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"><ArrowLeft size={16} /></Link>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-base font-semibold text-slate-800 dark:text-gray-100">{meta?.name}</h1>
                <Tag color={PROJECT_STATUS[project.status].tag} className="!m-0">{PROJECT_STATUS[project.status].label}</Tag>
              </div>
              <div className="text-xs text-slate-400"><span className="font-mono">{project.code}</span> · {DEPARTMENTS.find(d => d.id === meta?.departmentId)?.name}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!closed && <Button type="primary" icon={<Plus size={14} />} onClick={() => setModal('task')}>Thêm công việc</Button>}
            {primary.map(a => <Button key={a} onClick={() => run(a)}>{PROJECT_ACTION_LABEL[a]}</Button>)}
            <Dropdown trigger={['click']} menu={{
              items: [
                ...(manage ? [{ key: 'edit', label: 'Sửa thông tin' }, { key: 'members', label: 'Thành viên' }, { key: 'phases', label: 'Giai đoạn' }] : []),
                { key: 'open', label: 'Mở trong Công việc' },
                ...(more.length ? [{ type: 'divider' as const }, ...more.map(a => ({ key: a, label: PROJECT_ACTION_LABEL[a], danger: a === 'cancel' }))] : []),
              ],
              onClick: e => {
                if (e.key === 'open') navigate(`/work-list?project=${project.id}&view=all`);
                else if (e.key === 'edit' || e.key === 'members' || e.key === 'phases') setModal(e.key);
                else run(e.key as ProjectAction);
              },
            }}>
              <Button icon={<MoreHorizontal size={16} />} />
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="space-y-4 py-4">
        {project.status === 'paused' && <Alert type="warning" showIcon message={`Dự án đang tạm dừng${lastPause?.text ? ` — ${lastPause.text.split(' · ').slice(1).join(' · ') || 'không ghi lý do'}` : ''}`} action={actions.includes('resume') && <Button size="small" onClick={() => run('resume')}>Tiếp tục</Button>} />}
        {project.status === 'waiting' && <Alert type="info" showIcon message="Dự án chưa bắt đầu. Có thể chuẩn bị công việc trước, bấm Bắt đầu dự án khi triển khai." />}
        {lateDays > 0 && <Alert type="error" showIcon message={`Dự án đã quá thời gian dự kiến ${lateDays} ngày (kết thúc ${fmtDate(project.endAt)})`} action={manage && <Button size="small" onClick={() => setModal('edit')}>Gia hạn</Button>} />}
        {project.status === 'doing' && stats.total > 0 && !finishBlocker(project, allTasks) && <Alert type="success" showIcon message="Tất cả công việc đã đóng — có thể hoàn thành dự án." action={actions.includes('finish') && <Button size="small" type="primary" onClick={() => run('finish')}>Hoàn thành dự án</Button>} />}

        {/* KPI */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <div className="col-span-2 flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 md:col-span-3 xl:col-span-2">
            <Progress type="circle" size={64} percent={progress} strokeColor="#3e91ab" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium">Tiến độ {progress}%</div>
              <div className="text-xs text-slate-400">{PROGRESS_METHODS.find(m => m.value === project.progressMethod)?.label}</div>
              {totalDays > 0 && (
                <Tooltip title={`Đã qua ${timePct}% thời gian dự kiến`}>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 dark:bg-gray-700"><div className={`h-full rounded-full ${timePct > progress + 15 && !closed ? 'bg-amber-400' : 'bg-slate-300'}`} style={{ width: `${timePct}%` }} /></div>
                    thời gian {timePct}%
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <Kpi label="Tổng công việc" value={stats.total} sub={`${stats.total - stats.done - stats.failed} đang mở`} color="#334155" active={tab === 'tasks' && filter === 'all'} onClick={() => showTasks('all')} />
          <Kpi label="Chờ duyệt" value={stats.review} sub="đã báo xong" color="#a855f7" active={tab === 'tasks' && filter === 'review'} onClick={() => showTasks('review')} />
          <Kpi label="Quá hạn" value={stats.overdue} sub="chưa xong, đã qua hạn" color="#ef4444" active={tab === 'tasks' && filter === 'overdue'} onClick={() => showTasks('overdue')} />
          <Kpi label="Hoàn thành" value={stats.done} sub={stats.late ? `${stats.late} việc xong muộn` : 'đều đúng hạn'} color="#16a34a" active={tab === 'tasks' && filter === 'closed'} onClick={() => showTasks('closed')} />
        </div>

        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-8">
            <Card>
              <Tabs activeKey={tab} onChange={setTab} className="-mt-2" items={[
                {
                  key: 'tasks', label: `Công việc (${stats.total})`, children: (
                    <div className="space-y-3">
                      {outside > 0 && filter !== 'outside' && <Alert type="warning" showIcon message={`${outside} việc đang mở có lịch nằm ngoài thời gian dự án`} action={<Button size="small" onClick={() => setFilter('outside')}>Xem</Button>} />}
                      <div className="flex flex-wrap items-center gap-2">
                        <Segmented value={filter} onChange={v => setFilter(v as TaskFilter)}
                          options={TASK_FILTERS.filter(f => f.value !== 'outside' || outside > 0 || filter === 'outside').map(f => ({ value: f.value, label: `${f.label} ${tasks.filter(t => f.test(t, project)).length}` }))} />
                        <Input.Search allowClear placeholder="Tìm mã, tên việc" className="!w-52" value={q} onChange={e => setQ(e.target.value)} />
                        <Select allowClear showSearch optionFilterProp="label" placeholder="Người làm" className="w-48" value={assignee} onChange={setAssignee}
                          options={participants.map(p => ({ value: p, label: memberName(p) }))} />
                      </div>
                      <Table size="middle" rowKey="id" columns={columns} dataSource={shown} scroll={{ x: 700 }}
                        pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }}
                        onRow={t => ({ onClick: () => openTask(t.id), className: `cursor-pointer ${isOverdue(t) ? 'bg-red-50/40' : ''}` })}
                        locale={{ emptyText: <Empty description={filter === 'open' ? 'Không còn việc đang mở' : 'Không có công việc'}>{!closed && filter === 'open' && <Button icon={<Plus size={14} />} onClick={() => setModal('task')}>Thêm công việc</Button>}</Empty> }} />
                    </div>
                  ),
                },
                {
                  key: 'gantt', label: 'Tiến độ', children: tasks.length ? (
                    <div className="space-y-2">
                      <Checkbox checked={ganttAll} onChange={e => setGanttAll(e.target.checked)}>Hiện cả việc đã đóng quá 30 ngày</Checkbox>
                      <GanttView tasks={ganttAll ? tasks : tasks.filter(t => !isClosed(t) || (t.doneAt ?? t.dueAt ?? t.startAt ?? '') >= today.subtract(30, 'day').format('YYYY-MM-DD'))} onOpen={openTask} />
                    </div>
                  ) : <Empty description="Chưa có công việc" />,
                },
                {
                  key: 'members', label: `Thành viên (${participants.length})`, children: (
                    <div>
                      <div className="mb-3 flex items-center justify-between text-sm text-slate-500">
                        <span>Bấm một người để xem công việc của họ</span>
                        {manage && <Button size="small" icon={<Users size={14} />} onClick={() => setModal('members')}>Quản lý thành viên</Button>}
                      </div>
                      <div className="divide-y divide-slate-100 rounded-lg border border-slate-100 dark:divide-gray-700 dark:border-gray-700">
                        {workload.map(w => (
                          <button key={w.id} onClick={() => showTasks('all', w.id)} className="grid w-full grid-cols-[1fr_auto] items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-gray-700/40 sm:grid-cols-[minmax(0,1fr)_120px_200px]">
                            <span className="flex min-w-0 items-center gap-2"><Avatar id={w.id} size={30} /><span className="min-w-0"><span className="block truncate font-medium">{memberName(w.id)}</span><span className="text-xs text-slate-400">{w.role}</span></span></span>
                            <span className="text-right text-xs sm:text-left">
                              <b className="text-slate-700 dark:text-gray-200">{w.open}</b> đang mở
                              {w.overdue > 0 && <span className="text-red-500"> · {w.overdue} quá hạn</span>}
                            </span>
                            <span className="col-span-2 sm:col-span-1">{w.tasks.length ? <LoadBar tasks={w.tasks} /> : <span className="text-xs text-slate-400">Chưa có việc</span>}</span>
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
                        {[['#22c55e', 'Hoàn thành'], ['#3b82f6', 'Đang làm / chưa bắt đầu'], ['#a855f7', 'Chờ duyệt'], ['#ef4444', 'Quá hạn']].map(([c, l]) => <span key={l} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}</span>)}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'phases', label: `Giai đoạn (${project.phases.length})`, children: (
                    <div>
                      <div className="mb-3 flex items-center justify-between text-sm text-slate-500">
                        <span>Công việc được tính vào giai đoạn theo hạn chót</span>
                        {manage && <Button size="small" icon={<Pencil size={14} />} onClick={() => setModal('phases')}>Sửa giai đoạn</Button>}
                      </div>
                      {project.phases.length === 0 ? <Empty description="Chưa chia giai đoạn">{manage && <Button onClick={() => setModal('phases')}>Thêm giai đoạn</Button>}</Empty> : (
                        <div className="space-y-2">
                          {project.phases.map((ph, i) => {
                            const inPhase = tasks.filter(t => t.dueAt && (!ph.startAt || t.dueAt >= ph.startAt) && (!ph.endAt || t.dueAt <= ph.endAt));
                            const done = inPhase.filter(t => t.status === 'done').length;
                            const current = !!ph.startAt && !!ph.endAt && today.isAfter(dayjs(ph.startAt).subtract(1, 'day')) && today.isBefore(dayjs(ph.endAt).add(1, 'day'));
                            const late = !!ph.endAt && today.isAfter(ph.endAt, 'day') && inPhase.some(t => !isClosed(t));
                            return (
                              <div key={ph.id} className={`rounded-lg border p-3 ${current ? 'border-[#3e91ab] bg-[#3e91ab]/5' : 'border-slate-200 dark:border-gray-700'}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="font-medium"><span className="mr-2 text-slate-400">{i + 1}.</span>{ph.name}{current && <Tag color="blue" className="!ml-2">Đang diễn ra</Tag>}{late && <Tag color="red" className="!ml-2">Trễ</Tag>}</span>
                                  <span className="text-xs text-slate-500">{fmtDate(ph.startAt)} – {fmtDate(ph.endAt)}</span>
                                </div>
                                {ph.note && <div className="mt-1 text-xs text-slate-500">{ph.note}</div>}
                                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                                  <Progress className="!m-0 flex-1" percent={inPhase.length ? Math.round((done / inPhase.length) * 100) : 0} size="small" strokeColor="#3e91ab" />
                                  <span className="shrink-0">{done}/{inPhase.length} việc</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ),
                },
              ]} />
            </Card>
          </div>

          <div className="space-y-4 lg:col-span-4">
            <Card title="Thông tin chung" extra={manage && <Button size="small" type="text" icon={<Pencil size={14} />} onClick={() => setModal('edit')}>Sửa</Button>}>
              <Info label="Quản trị"><span className="flex flex-wrap gap-2">{project.ownerIds.map(o => <Avatar key={o} id={o} size={22} withName />)}</span></Info>
              <Info label="Thành viên">
                <button onClick={() => setTab('members')} className="flex items-center -space-x-1.5">
                  {participants.slice(0, 8).map(p => <span key={p} className="rounded-full ring-2 ring-white dark:ring-gray-800"><Avatar id={p} size={24} /></span>)}
                  {participants.length > 8 && <span className="pl-3 text-xs text-slate-500">+{participants.length - 8}</span>}
                </button>
              </Info>
              {project.followerIds.length > 0 && <Info label="Theo dõi">{project.followerIds.map(memberName).join(', ')}</Info>}
              <Info label="Dự kiến">
                {fmtDate(project.startAt)} – {fmtDate(project.endAt)} <span className="text-slate-400">({totalDays} ngày)</span>
                {!closed && project.endAt && lateDays <= 0 && <div className="text-xs text-slate-500">còn {-lateDays} ngày</div>}
              </Info>
              <Info label="Thực tế">
                {project.actualStartAt ? <>{fmtDate(project.actualStartAt)} – {project.actualEndAt ? fmtDate(project.actualEndAt) : <span className="text-slate-400">nay</span>}</> : <span className="text-slate-400">Chưa bắt đầu</span>}
              </Info>
              <Info label="Quy tắc">
                <ul className="space-y-1 text-xs text-slate-600 dark:text-gray-300">
                  <li>{project.syncTaskDates ? '✓' : '✗'} Dời lịch dự án thì dời lịch công việc</li>
                  <li>{project.taskWithinProject ? '✓' : '✗'} Công việc nằm trong thời gian dự án</li>
                </ul>
              </Info>
              <Info label="Mô tả"><span className="whitespace-pre-line">{project.description || <span className="text-slate-400">—</span>}</span></Info>
            </Card>

            <Card>
              <Segmented block className="mb-3" value={side} onChange={v => setSide(v as typeof side)}
                options={[{ value: 'activity', label: 'Hoạt động' }, { value: 'discussion', label: `Thảo luận (${comments.length})` }]} />
              {side === 'activity' ? (
                <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1 text-sm">
                  {feed.map(a => (
                    <div key={a.id} className="flex gap-2">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: a.color }} />
                      <div className="min-w-0">
                        <b>{memberName(a.by)}</b> {a.text}
                        <div className="text-xs text-slate-400">{dayjs(a.at).format('DD/MM/YYYY HH:mm')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="max-h-[360px] space-y-3 overflow-y-auto pr-1">
                    {comments.length === 0 && <div className="py-4 text-center text-sm text-slate-400">Chưa có thảo luận</div>}
                    {comments.map(c => (
                      <div key={c.id} className="flex gap-2 text-sm">
                        <Avatar id={c.by} size={26} />
                        <div className="min-w-0 flex-1">
                          <div><b>{memberName(c.by)}</b> <span className="text-xs text-slate-400">{dayjs(c.at).format('DD/MM HH:mm')}</span></div>
                          <div className="mt-0.5 whitespace-pre-line rounded-lg bg-slate-50 px-3 py-2 dark:bg-gray-700/50">{c.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-end gap-2">
                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} value={text} onChange={e => setText(e.target.value)} placeholder="Viết thảo luận… (Enter để gửi)"
                      onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); sendComment(); } }} />
                    <Button type="primary" icon={<Send size={14} />} disabled={!text.trim()} onClick={sendComment} />
                  </div>
                </div>
              )}
            </Card>

            <Card title={`Tài liệu (${project.links.length})`}>
              <div className="space-y-2">
                {project.links.map(l => (
                  <div key={l.id} className="group flex items-center gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-sm dark:border-gray-700">
                    <Link2 size={16} className="shrink-0 text-[#3e91ab]" />
                    <a href={l.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline">{l.name}</a>
                    <span className="hidden text-xs text-slate-400 sm:inline">{memberName(l.by).split(' ').pop()}</span>
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#3e91ab]"><ExternalLink size={14} /></a>
                    {(l.by === ME_ID || manage) && <button onClick={() => removeLink(project.id, l.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
                  </div>
                ))}
                {project.links.length === 0 && <div className="text-sm text-slate-400">Gắn link Google Drive, tài liệu, thiết kế… của dự án</div>}
                <div className="space-y-1.5 pt-1">
                  <Input size="small" placeholder="Tên tài liệu" value={link.name} onChange={e => setLink({ ...link, name: e.target.value })} />
                  <div className="flex gap-1.5">
                    <Input size="small" placeholder="https://…" status={link.url && !validUrl ? 'error' : undefined} value={link.url} onChange={e => setLink({ ...link, url: e.target.value })} />
                    <Button size="small" disabled={!link.name.trim() || !validUrl} onClick={() => { addLink(project.id, link.name.trim(), link.url.trim(), ME_ID); setLink({ name: '', url: '' }); }}>Thêm</Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <StatusModal key={action ?? 'none'} project={project} action={action} onClose={() => setAction(null)} />
      <EditModal project={project} open={modal === 'edit'} onClose={() => setModal(null)} />
      <MembersModal project={project} open={modal === 'members'} onClose={() => setModal(null)} />
      <PhasesModal project={project} open={modal === 'phases'} onClose={() => setModal(null)} />
      <TaskForm open={modal === 'task'} onClose={() => setModal(null)} projectId={project.id} onCreated={t => openTask(t.id)} />
      <TaskDetail taskId={taskId} onClose={() => setParam('task', null)} onOpen={openTask} />
    </Layout>
  );
}
